import { Component, OnInit } from "@angular/core";
import { Title } from "@angular/platform-browser";
import { Router } from "@angular/router";
import { SubmissionStatusService } from "@app/services/submission-status.service";
import { TranslateService } from "@ngx-translate/core";
declare let gtag: Function;

@Component({
  selector: "nh-appeals-status-tracker",
  templateUrl: "./appeals-status-tracker.component.html",
  styleUrl: "./appeals-status-tracker.component.scss",
  standalone: false
})
export class AppealsStatusTrackerComponent implements OnInit {
  expandedIndex: number | null = null;
  initialStatuses = ['APPEAL REGISTRATION', 'APPEAL UNDER REVIEW', 'SCHEDULING HEARING', "HEARING HELD - AWAITING JUDGE'S DECISION", 'CLOSED']

  // Each Set is one stepper slot; alternatives within a slot are mutually exclusive
  private readonly slotGroups: Set<string>[] = [
    new Set(['SUBMITTED', 'APPEAL REGISTRATION']),
    new Set(['APPEAL UNDER REVIEW', 'INFORMATION NEEDED']),
    new Set(['SCHEDULING HEARING', 'NOTICE OF HEARING SENT']),
    new Set(["HEARING HELD - AWAITING JUDGE'S DECISION", 'ORDER ISSUED', 'PETITION PENDING']),
    new Set(['CLOSED']),
  ];

  private getSlot(status: string): number {
    const upper = status.toUpperCase();
    return this.slotGroups.findIndex(group => group.has(upper));
  }
  appDetails: any[] = [];
  translatedLang: string;
  globalLanguage: string;
  paginatedData: any[] = [];
  pageSize = 5;
  currentPage = 1;
  totalPages = 1;
  visiblePagesLength = 7;
  pages: number[] = [];
  constructor(private router: Router, public submissionStatusService: SubmissionStatusService, private titleService: Title, public translateService: TranslateService) {
    this.titleService.setTitle('Submission Status');
  }

  ngOnInit(): void {
    const indiv = sessionStorage.getItem('individualId');
    this.submissionStatusService.getAppealsStatus(indiv).subscribe((res: any) => {
      this.appDetails = res.appealsStatusData.map((record: any) => ({
        ...record,
        currentAppealStatus: this.extractCurrentStatus(record?.appealStatus),
        submissionDateOnly: this.extractDate(record?.dateOfSubmission)
      }));
      this.loadData();
    })

    this.translatedLang = this.translateService.getDefaultLang();
    this.globalLanguage = sessionStorage.getItem('globalTranslatedLan')
    this.translateService.onDefaultLangChange.subscribe((res) => {
      this.translatedLang = res.lang;
    });
  }

  loadData() {
    this.totalPages = Math.ceil(this.appDetails.length / this.pageSize);
    this.pages = Array.from({ length: this.totalPages }, (_, i) => i + 1);
    this.updatePageWindow();
    this.updatePagination();
  }

  updatePagination() {
    const start = (this.currentPage - 1) * this.pageSize;
    const end = start + this.pageSize;
    this.paginatedData = this.appDetails.slice(start, end);
  }

  changePage(page: number) {
    if (page >= 1 && page <= this.totalPages) {
      this.currentPage = page;
      this.expandedIndex = null;
      this.updatePageWindow();
      this.updatePagination();
    }
  }

  toggleExpand(index: number, appealNum?: string): void {
    if (this.expandedIndex !== index) {
      gtag('event', 'appeal_status_tracker_expanded', {
        'event_category': 'appeal_status_tracker_expanded',
        'event_label': 'appeal_status_tracker_expanded',
        'value': appealNum
      });
    }
    this.expandedIndex = this.expandedIndex === index ? null : index;
  }

  getStatusClass(status: string, currentStatus: string): string {
    const normalizedCurrent = currentStatus.toUpperCase().replace(/HEARING HELD-\s*/i, 'HEARING HELD - ');

    if (normalizedCurrent === 'INFORMATION NEEDED') {
      if (status.toUpperCase() === 'APPEAL REGISTRATION') {
        return 'past-status';
      }
      if (status.toUpperCase() === 'INFORMATION NEEDED') {
        return 'highlighted-red';
      }
      const order = this.getDisplayStatus(normalizedCurrent);
      const currentIndex = order.findIndex(s => s.toUpperCase() === 'INFORMATION NEEDED');
      const statusIndex = order.indexOf(status);

      if (statusIndex === currentIndex + 1) return 'pending';
      return 'upcoming';
    } else if (normalizedCurrent === 'CLOSED') {
      return 'past-status';
    }
    // Normal flow
    const order = this.getDisplayStatus(normalizedCurrent);
    const currentIndex = order.findIndex(s => s.toUpperCase() === normalizedCurrent);
    const statusIndex = order.indexOf(status);

    if (statusIndex < currentIndex) return 'past-status';
    if (statusIndex === currentIndex) return 'current-status';
    if (statusIndex === currentIndex + 1) return 'pending';
    return 'upcoming';
  }


  shouldShowClock(status: string, currentStatus: string): boolean {
    const cls = this.getStatusClass(status, currentStatus);
    const currentStepperStatus = currentStatus.toUpperCase();
    if (currentStepperStatus === 'SUBMITTED' || currentStepperStatus === 'APPEAL REGISTRATION') {
      return cls === 'upcoming';

    }
    return cls === 'pending' || cls === 'upcoming';
  }

  shouldShowDashed(status: string, currentStatus: string): boolean {
    const cls = this.getStatusClass(status, currentStatus);
    return cls === 'pending' || cls === 'upcoming';
  }

  hasUpcomingSteps(currentStatus: string): boolean {
    const order = this.getDisplayStatus(currentStatus);
    return order.some(s => {
      const cls = this.getStatusClass(s, currentStatus);
      return cls === 'pending' || cls === 'upcoming';
    });
  }

  getStepStyle(status: string, currentStatus: string, index: number, totalSteps?: number) {
    const cls = this.getStatusClass(status, currentStatus);
    const lastIndex = (totalSteps ?? this.getDisplayStatus(currentStatus).length) - 1;
    const isSolid = ['past-status', 'current-status', 'highlighted-red'].includes(cls);

    if (!isSolid || index === 0) {
      return {};
    }

    if (index === lastIndex) {
      return { 'clip-path': 'polygon(22px 50%, 0% 0%, 100% 0%, 100% 100%, 0% 100%)' };
    }

    return { 'clip-path': 'polygon(0% 0%, calc(100% - 22px) 0%, 100% 50%, calc(100% - 22px) 100%, 0% 100%, 22px 50%)' };
  }

  private extractCurrentStatus(appealStatus: string): string {
    const parts = (appealStatus ?? '').split(',').map(s => s.trim());
    if (parts.some(p => p.toUpperCase() === 'CLOSED')) return 'Closed';
    return parts[0] ?? '';
  }

  getTrackerSteps(record: any): string[] {
    const current = (record.currentAppealStatus ?? '').toUpperCase();
    if (current === 'CLOSED') return this.parseClosedSteps(record.appealStatus);
    return this.getDisplayStatus(current);
  }

  getDisplayStatus(status: string): string[] {
    if (status.toUpperCase() === 'INFORMATION NEEDED') {
      return ['APPEAL REGISTRATION', 'INFORMATION NEEDED', 'SCHEDULING HEARING', "HEARING HELD - AWAITING JUDGE'S DECISION", 'CLOSED'];
    } else if (status.toUpperCase() === 'NOTICE OF HEARING SENT') {
      return ['APPEAL REGISTRATION', 'APPEAL UNDER REVIEW', 'NOTICE OF HEARING SENT', "HEARING HELD - AWAITING JUDGE'S DECISION", 'CLOSED'];
    } else if (status.toUpperCase() === 'ORDER ISSUED') {
      return ['APPEAL REGISTRATION', 'APPEAL UNDER REVIEW', 'SCHEDULING HEARING', 'ORDER ISSUED', 'CLOSED'];
    } else if (status.toUpperCase() === 'PETITION PENDING') {
      return ['APPEAL REGISTRATION', 'APPEAL UNDER REVIEW', 'SCHEDULING HEARING', 'PETITION PENDING', 'CLOSED'];
    } else if (status.toUpperCase() === 'SUBMITTED') {
      return ['SUBMITTED', 'APPEAL UNDER REVIEW', 'SCHEDULING HEARING', "HEARING HELD - AWAITING JUDGE'S DECISION", 'CLOSED']
    }
    return this.initialStatuses;
  }

  private parseClosedSteps(appealStatus: string): string[] {
    const parts = (appealStatus ?? '').split(',').map(s => s.trim());
    // Drop any statuses newer than the first CLOSED entry (post-closure re-opens are irrelevant)
    const closedIdx = parts.findIndex(p => p.toUpperCase() === 'CLOSED');
    const relevantParts = closedIdx >= 0 ? parts.slice(closedIdx) : parts;
    const seenSlots = new Set<number>();
    const slotDeduped = relevantParts.filter(p => {
      const slot = this.getSlot(p);
      if (slot < 0) return false;
      if (seenSlots.has(slot)) return false;
      seenSlots.add(slot);
      return true;
    });
    return [...slotDeduped].reverse().map(s => s.toUpperCase());
  }

  private readonly statusLabelMap: { [key: string]: string } = {
    'SUBMITTED': 'APPEALS_TRACKER_SUBMITTED',
    'APPEAL REGISTRATION': 'APPEALS_TRACKER_RIP',
    'APPEAL UNDER REVIEW': 'APPEALS_TRACKER_AIUR',
    'INFORMATION NEEDED': 'APPEALS_TRACKER_IN',
    'SCHEDULING HEARING': 'APPEALS_TRACKER_SH',
    'NOTICE OF HEARING SENT': 'APPEALS_TRACKER_NOHS',
    "HEARING HELD - AWAITING JUDGE'S DECISION": 'APPEALS_TRACKER_HH_AJD',
    'ORDER ISSUED': 'APPEALS_TRACKER_OI',
    'PETITION PENDING': 'APPEALS_TRACKER_PP',
    'CLOSED': 'APPEALS_TRACKER_CLOSED',
  };

  getStatusLabel(status: string): string {
    return this.statusLabelMap[status.toUpperCase()] || status;
  }

  getPopoverLeftOffset(currentStatus: string): string {
    const normalized = currentStatus.toUpperCase();
    const order = this.getDisplayStatus(normalized);
    const currentIndex = order.findIndex(s =>
      this.getStatusClass(s, normalized) === 'current-status' ||
      this.getStatusClass(s, normalized) === 'highlighted-red'
    );
    if (currentIndex < 0) return '0%';
    // SUBMITTED/APPEAL REGISTRATION: pending step shows text (no clock), skip to first clock step
    const offset = (normalized === 'SUBMITTED' || normalized === 'APPEAL REGISTRATION') ? 2 : 1;
    return `${((currentIndex + offset) / order.length) * 100}%`;
  }

  goToEligibilityAppeals(appealNum?: string) {
    gtag('event', 'appeal_status_tracker_go_to_eligibility_appeals', {
      'event_category': 'appeal_status_tracker_go_to_eligibility_appeals',
      'event_label': 'appeal_status_tracker_go_to_eligibility_appeals',
      'value': appealNum
    });
    this.router.navigate(['/user-profile', 'my-appeals']);
  }

  goToDashboard(appealNum?: string) {
    gtag('event', 'appeal_status_tracker_go_to_dashboard', {
      'event_category': 'appeal_status_tracker_go_to_dashboard',
      'event_label': 'appeal_status_tracker_go_to_dashboard',
      'value': appealNum
    });
    this.router.navigateByUrl("user-profile/benefit-dashboard");
  }

  goToMyLetters(appealNum?: string) {
    gtag('event', 'appeal_status_tracker_go_to_my_letters', {
      'event_category': 'appeal_status_tracker_go_to_my_letters',
      'event_label': 'appeal_status_tracker_go_to_my_letters',
      'value': appealNum
    });
    this.router.navigate(['user-profile/letters/my-letters']);
  }

  goToDocuments(appealNum?: string) {
    gtag('event', 'appeal_status_tracker_go_to_documents', {
      'event_category': 'appeal_status_tracker_go_to_documents',
      'event_label': 'appeal_status_tracker_go_to_documents',
      'value': appealNum
    });
    this.router.navigate(['/user-profile', 'my-documents']);
  }

  /* Updating the visible page numbers */
  updatePageWindow(): void {
    this.pages = [];
    let start = this.currentPage;
    // Ensuring that if we're near the end, we don't go past totalPages
    if (start + this.visiblePagesLength - 1 > this.totalPages) {
      start = Math.max(1, this.totalPages - this.visiblePagesLength + 1);
    }
    // building the visible page list
    for (let i = 0; i < this.visiblePagesLength; i++) {
      const page = start + i;
      if (page <= this.totalPages) {
        this.pages.push(page);
      }
    }
  }

  extractDate(dateTime: string): string {
    return dateTime.split(' ')[0];
  }
}
